<script>
import { mapGetters } from 'vuex';
import InventoryService from '@/services/InventoryService';
import SettingsService from '@/services/SettingsService';
import { FormUtils } from '../../utils/FormUtils';

export default {
  name: 'DeliveryDistributionTable',
  props: {
    saleFolio: Number,
    pickupStoreId: Number,
  },
  data: () => ({
    deliveryTableHeaders: [
      {
        text: 'Producto',
        value: 'name',
        align: 'start',
        sortable: false,
      },
      {
        text: 'Total',
        value: 'quantity',
        align: 'start',
        sortable: false,
      },
      {
        text: 'Estatus',
        value: 'status',
        align: 'center',
        sortable: false,
      },
    ],
    inventory: [],
    pickupDistribution: [],
    nameHeader: 'name',
    quantityHeader: 'quantity',
    statusHeader: 'status',
  }),
  computed: {
    ...mapGetters({
      stores: 'stores',
      itemsWithNoInventory: 'itemsWithNoInventory',
    }),
  },
  methods: {
    allowOnlyNumericInput(e) {
      return FormUtils.allowOnlyNumericInput(e);
    },
    hasDistributionData() {
      return this.pickupDistribution.length > 0;
    },
    itemsToDistribute() {
      return this.pickupDistribution.length;
    },
    getStoreInventory(productId, storeId) {
      const entry = this.inventory.find(
        (inv) => Number(inv.productId) === Number(productId) && Number(inv.storeId) === Number(storeId),
      );
      return entry ? Number(entry.quantity) : 0;
    },
    hasSufficientInventory(item) {
      return this.stores.every((store) => {
        const assigned = Number(item[`storeId${store.id}`]) || 0;
        if (this.inventory.length === 0) return true;
        return assigned <= this.getStoreInventory(item.id, store.id);
      });
    },
    requiresConfirmation() {
      return this.pickupDistribution.some((item) => !this.hasSufficientInventory(item));
    },
    getChipColor(item) {
      const assignedQuantity = this.getItemAssignedQuantity(item);
      if (assignedQuantity === 0) {
        return 'error';
      }
      if (assignedQuantity < item.quantity) {
        return 'warning';
      }
      if (assignedQuantity === item.quantity) {
        return 'success';
      }
      return '';
    },
    initializePickupDistribution(items = null) {
      const itemsToDistribute = items || this.itemsWithNoInventory;
      const productMap = itemsToDistribute.map((item) => ({
        id: item.id,
        sku: item.barcode,
        name: item.name,
        quantity: item.backorder,
        status: 'unset',
      }));
      this.stores.forEach((store) => {
        for (let i = 0; i < productMap.length; i += 1) {
          if (itemsToDistribute[i][`storeId${store.id}`] !== undefined) {
            productMap[i][`storeId${store.id}`] = itemsToDistribute[i][`storeId${store.id}`];
          } else if (store.id === this.pickupStoreId) {
            productMap[i][`storeId${store.id}`] = productMap[i].quantity;
          } else {
            productMap[i][`storeId${store.id}`] = 0;
          }
        }
      });
      this.pickupDistribution = productMap;
    },
    sendAllToCurrentStore() {
      if (!this.pickupStoreId) return;
      /* eslint-disable no-param-reassign */
      this.pickupDistribution.forEach((item) => {
        this.stores.forEach((store) => {
          item[`storeId${store.id}`] = store.id === this.pickupStoreId ? item.quantity : 0;
        });
      });
    },
    getItemAssignedQuantity(item) {
      let total = 0;
      this.stores.forEach((store) => {
        const val = Number(item[`storeId${store.id}`]);
        total += (Number.isNaN(val) || val < 0) ? 0 : val;
      });
      return total;
    },
    validateAssignedQuantity(quantity, item, storeKey = null) {
      const parsedVal = parseInt(quantity, 10);
      const numQuantity = Number.isNaN(parsedVal) || parsedVal < 0 ? 0 : parsedVal;

      if (!storeKey) {
        const assignedTotal = this.getItemAssignedQuantity(item);
        const assignedBase = assignedTotal - numQuantity;
        const remainingSpace = Math.max(0, item.quantity - assignedBase);
        return Math.min(numQuantity, remainingSpace);
      }

      let assignedOtherStores = 0;
      this.stores.forEach((store) => {
        const key = `storeId${store.id}`;
        if (key !== storeKey) {
          const val = Number(item[key]);
          assignedOtherStores += (Number.isNaN(val) || val < 0) ? 0 : val;
        }
      });

      const remainingSpace = Math.max(0, item.quantity - assignedOtherStores);
      const validQuantity = Math.min(numQuantity, remainingSpace);

      this.$set(item, storeKey, validQuantity);

      if (String(quantity) !== String(validQuantity)) {
        this.$nextTick(() => {
          this.$set(item, storeKey, null);
          this.$nextTick(() => {
            this.$set(item, storeKey, validQuantity);
          });
        });
      }

      return validQuantity;
    },
    validateForm() {
      return this.pickupDistribution.some((pickup) => {
        const assignedTotal = this.getItemAssignedQuantity(pickup);
        return pickup.quantity > assignedTotal;
      });
    },
    applyPickupStoreToProducts(products) {
      const buckets = new Map();

      const assignedProducts = products.map((originalProduct) => ({
        ...originalProduct,
        pickupStoreId: null,
      }));

      assignedProducts.forEach((product) => {
        const productKey = String(product?.productId ?? '');
        if (!buckets.has(productKey)) {
          buckets.set(productKey, []);
        }
        buckets.get(productKey).push(product);
      });

      // Use pickupDistribution to assign products to different stores
      this.pickupDistribution.forEach((distribution) => {
        const productKey = String(distribution?.id ?? '');
        const availableProducts = buckets.get(productKey) || [];

        // Iterate over each store field in distribution
        Object.keys(distribution).forEach((key) => {
          if (key.startsWith('storeId')) {
            const storeId = Number(key.replace('storeId', ''));
            const quantityToAssign = Math.max(0, Number(distribution[key]));

            for (
              let assigned = 0;
              assigned < quantityToAssign && availableProducts.length > 0;
              assigned += 1
            ) {
              const product = availableProducts.shift();
              product.pickupStoreId = storeId;
            }
          }
        });
      });
      return assignedProducts;
    },
    getAssignedData(products) {
      return {
        products: this.applyPickupStoreToProducts(products),
        requiresConfirmation: this.requiresConfirmation(),
      };
    },
    async reset() {
      this.pickupDistribution = [];
      const inventory = await InventoryService.getSaleProductsInventory(this.saleFolio);
      this.inventory = inventory;
    },
  },
  async mounted() {
    if (this.stores === null || this.stores.length === 0) {
      const stores = await SettingsService.getStores();
      this.$store.dispatch('setStores', stores);
    }
    [...this.stores].sort((storeA, storeB) => {
      const getStorePriority = (store) => {
        if (store.id === this.pickupStoreId) return 0;
        return store.isWarehouse ? 2 : 1;
      };
      const priorityDifference = getStorePriority(storeA) - getStorePriority(storeB);
      if (priorityDifference !== 0) return priorityDifference;
      return Number(storeA.id) - Number(storeB.id);
    }).forEach((store, index) => {
      this.deliveryTableHeaders.splice(2 + index, 0, {
        text: store.name,
        value: `storeId${store.id}`,
        align: 'center',
      });
    });
    const inventory = await InventoryService.getSaleProductsInventory(this.saleFolio);
    this.inventory = inventory;
  },
};
</script>

<template>
<v-data-table
  :headers="deliveryTableHeaders"
  :items="pickupDistribution"
  hide-default-footer
  class="elevation-2"
>
  <template v-slot:item.total>
    {{ item.quantity }}
  </template>
  <template
    v-for="header in deliveryTableHeaders"
    v-slot:[`item.${header.value}`]="{ item }"
  >
    <div
      v-if="header.value.startsWith('storeId')"
      :key="header.value"
      class="d-flex align-center"
    >
      <v-text-field
        :value="item[`${header.value}`]"
        @input="validateAssignedQuantity($event, item, header.value)"
        type="number"
        min="0"
        :max="item.quantity"
        @keydown="allowOnlyNumericInput"
        dense
        outlined
        hide-details
      ></v-text-field>
    </div>
    <!-- eslint-disable vue/valid-v-for -->
    <div
      v-else-if="header.value == 'name'"
      :key="nameHeader"
      class="d-flex flex-column"
    >
      <span>{{ item.name }}</span>
      <span class="text-caption" v-if="item.sku">{{ item.sku }}</span>
    </div>
    <!-- eslint-disable vue/valid-v-for -->
    <div v-else-if="header.value == 'quantity'" :key="quantityHeader">
      <span>{{ item.quantity }}</span>
    </div>
    <!-- eslint-disable vue/valid-v-for -->
    <div v-else-if="header.value == 'status'" :key="statusHeader">
      <v-chip
        dense
        :color="getChipColor(item)"
      >
        {{ getItemAssignedQuantity(item) }} / {{ item.quantity }}
      </v-chip>
    </div>
  </template>
</v-data-table>
</template>
